import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  deleteSchoolById,
  getAllSchools,
} from "../common/SchoolAPI";

import "../css/AllSchools.css";

export default function AllSchools() {
  const [schools, setSchools] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  /* ======================================================
     FETCH SCHOOLS
  ====================================================== */

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setIsLoading(true);
        setError("");

        const data = await getAllSchools();

        setSchools(
          Array.isArray(data) ? data : []
        );
      } catch (error) {
        console.error(
          "Error fetching schools:",
          error
        );

        setError(
          "Unable to load schools right now. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchools();
  }, []);

  /* ======================================================
     FILTER VALID SCHOOLS
  ====================================================== */

  const validSchools = useMemo(() => {
    return schools.filter(
      (school) => school?.schoolName
    );
  }, [schools]);

  /* ======================================================
     SEARCH
  ====================================================== */

  const filteredSchools = useMemo(() => {
    const search =
      searchTerm.trim().toLowerCase();

    if (!search) {
      return validSchools;
    }

    return validSchools.filter((school) => {
      const id = String(
        school?.id ?? ""
      ).toLowerCase();

      const name =
        school?.schoolName
          ?.toLowerCase() || "";

      const address =
        school?.schoolAddress
          ?.toLowerCase() || "";

      return (
        id.includes(search) ||
        name.includes(search) ||
        address.includes(search)
      );
    });
  }, [validSchools, searchTerm]);

  /* ======================================================
     TOTAL STUDENTS
  ====================================================== */

  const totalStudents = useMemo(() => {
    return validSchools.reduce(
      (total, school) =>
        total +
        (Array.isArray(school.studentList)
          ? school.studentList.length
          : 0),
      0
    );
  }, [validSchools]);

  /* ======================================================
     DELETE SCHOOL
  ====================================================== */

  const deleteSchool = async (id, schoolName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${
        schoolName || "this school"
      }"?`
    );

    if (!confirmDelete) return;

    try {
      setDeletingId(id);

      await deleteSchoolById(id);

      /*
        Remove the deleted school locally.
        No full browser reload required.
      */
      setSchools((currentSchools) =>
        currentSchools.filter(
          (school) => school.id !== id
        )
      );
    } catch (error) {
      console.error(
        "Error deleting school:",
        error
      );

      window.alert(
        "Unable to delete the school. Please try again."
      );
    } finally {
      setDeletingId(null);
    }
  };

  /* ======================================================
     NAVIGATION
  ====================================================== */

  const handleBack = () => {
    navigate("/progress-tracking-main");
  };

  const handleAllStudents = (id) => {
    navigate(
      `/all-students-admin/${id}`
    );
  };

  const handleAddStudent = (id) => {
    navigate(
      `/add-student/${id}`
    );
  };

  const handleEdit = (id) => {
    navigate(
      `/edit-school/${id}`
    );
  };

  return (
    <main className="ms-school-admin-page">
      <div className="ms-school-admin-container">
        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <header className="ms-school-admin-header">
          <div className="ms-school-admin-header-left">
            <button
              type="button"
              className="ms-school-admin-back"
              onClick={handleBack}
            >
              <span>←</span>
              Back
            </button>

            <div className="ms-school-admin-eyebrow">
              <span />

              SCHOOL MANAGEMENT
            </div>

            <h1>
              All Schools
            </h1>

            <p>
              Manage schools, student records and
              school information from one place.
            </p>
          </div>

          <div className="ms-school-admin-brand">
            <div className="ms-school-admin-brand-mark">
              <span />
              <span />
            </div>

            <div>
              <small>
                MINDSPARK
              </small>

              <strong>
                Admin Workspace
              </strong>
            </div>
          </div>
        </header>

        {/* =================================================
            OVERVIEW
        ================================================= */}

        <section className="ms-school-admin-overview">
          <article className="ms-school-admin-stat yellow">
            <div className="ms-school-stat-icon">
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M3 10 12 5l9 5-9 5-9-5Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />

                <path
                  d="M6 13v5M18 13v5M4 19h16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div>
              <span>
                TOTAL SCHOOLS
              </span>

              <strong>
                {validSchools.length}
              </strong>
            </div>
          </article>

          <article className="ms-school-admin-stat blue">
            <div className="ms-school-stat-icon">
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  cx="9"
                  cy="8"
                  r="3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />

                <path
                  d="M3.5 19c.4-3.5 2.4-5.5 5.5-5.5s5.1 2 5.5 5.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />

                <path
                  d="M16 8a2.5 2.5 0 1 1 0 5M16.5 14.5c2.2.3 3.5 1.8 4 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div>
              <span>
                TOTAL STUDENTS
              </span>

              <strong>
                {totalStudents.toLocaleString()}
              </strong>
            </div>
          </article>

          <article className="ms-school-admin-stat purple">
            <div className="ms-school-stat-icon">
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />

                <path
                  d="m16 16 4 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div>
              <span>
                RESULTS
              </span>

              <strong>
                {filteredSchools.length}
              </strong>
            </div>
          </article>
        </section>

        {/* =================================================
            TABLE PANEL
        ================================================= */}

        <section className="ms-school-table-panel">
          {/* TOOLBAR */}

          <div className="ms-school-table-toolbar">
            <div>
              <h2>
                School Directory
              </h2>

              <p>
                View and manage registered
                schools.
              </p>
            </div>

            <div className="ms-school-admin-search">
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />

                <path
                  d="m16 16 4 4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>

              <input
                type="search"
                value={searchTerm}
                onChange={(event) =>
                  setSearchTerm(
                    event.target.value
                  )
                }
                placeholder="Search school, address or ID..."
                aria-label="Search schools"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={() =>
                    setSearchTerm("")
                  }
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* =================================================
              LOADING
          ================================================= */}

          {isLoading && (
            <div className="ms-school-admin-state">
              <div className="ms-school-admin-loader">
                <span />
                <span />
                <span />
              </div>

              <h3>
                Loading schools
              </h3>

              <p>
                Fetching school information...
              </p>
            </div>
          )}

          {/* =================================================
              ERROR
          ================================================= */}

          {!isLoading && error && (
            <div className="ms-school-admin-state error">
              <div className="ms-school-state-icon">
                !
              </div>

              <h3>
                Unable to load schools
              </h3>

              <p>
                {error}
              </p>

              <button
                type="button"
                onClick={() =>
                  window.location.reload()
                }
              >
                Try Again
              </button>
            </div>
          )}

          {/* =================================================
              DESKTOP TABLE
          ================================================= */}

          {!isLoading &&
            !error &&
            filteredSchools.length > 0 && (
              <>
                <div className="ms-school-results-info">
                  <span>
                    Showing{" "}
                    <strong>
                      {
                        filteredSchools.length
                      }
                    </strong>{" "}
                    of{" "}
                    <strong>
                      {validSchools.length}
                    </strong>{" "}
                    schools
                  </span>

                  {searchTerm && (
                    <small>
                      Search: “{searchTerm}”
                    </small>
                  )}
                </div>

                <div className="ms-school-table-scroll">
                  <table className="ms-school-table">
                    <thead>
                      <tr>
                        <th>
                          School ID
                        </th>

                        <th>
                          School
                        </th>

                        <th>
                          Address
                        </th>

                        <th>
                          Students
                        </th>

                        <th>
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredSchools.map(
                        (school, index) => {
                          const studentCount =
                            Array.isArray(
                              school.studentList
                            )
                              ? school
                                  .studentList
                                  .length
                              : 0;

                          return (
                            <tr key={school.id}>
                              <td>
                                <span className="ms-school-id">
                                  {school.id}
                                </span>
                              </td>

                              <td>
                                <div className="ms-school-name-cell">
                                  <div className="ms-school-avatar">
                                    {String(
                                      index + 1
                                    ).padStart(
                                      2,
                                      "0"
                                    )}
                                  </div>

                                  <div>
                                    <strong>
                                      {
                                        school.schoolName
                                      }
                                    </strong>

                                    <small>
                                      Partner School
                                    </small>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <div className="ms-school-address-cell">
                                  <svg
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M12 21s6-5.3 6-11a6 6 0 1 0-12 0c0 5.7 6 11 6 11Z"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.7"
                                    />

                                    <circle
                                      cx="12"
                                      cy="10"
                                      r="2"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.7"
                                    />
                                  </svg>

                                  <span>
                                    {school.schoolAddress ||
                                      "Address not available"}
                                  </span>
                                </div>
                              </td>

                              <td>
                                <button
                                  type="button"
                                  className="ms-student-count"
                                  onClick={() =>
                                    handleAllStudents(
                                      school.id
                                    )
                                  }
                                  title="View students"
                                >
                                  <span>
                                    {studentCount}
                                  </span>

                                  Students
                                </button>
                              </td>

                              <td>
                                <div className="ms-school-actions">
                                  <button
                                    type="button"
                                    className="add"
                                    onClick={() =>
                                      handleAddStudent(
                                        school.id
                                      )
                                    }
                                  >
                                    <span>
                                      +
                                    </span>

                                    Add Student
                                  </button>

                                  <button
                                    type="button"
                                    className="view"
                                    onClick={() =>
                                      handleAllStudents(
                                        school.id
                                      )
                                    }
                                  >
                                    View
                                  </button>

                                  <button
                                    type="button"
                                    className="edit"
                                    onClick={() =>
                                      handleEdit(
                                        school.id
                                      )
                                    }
                                  >
                                    Edit
                                  </button>

                                  <button
                                    type="button"
                                    className="delete"
                                    disabled={
                                      deletingId ===
                                      school.id
                                    }
                                    onClick={() =>
                                      deleteSchool(
                                        school.id,
                                        school.schoolName
                                      )
                                    }
                                  >
                                    {deletingId ===
                                    school.id
                                      ? "Deleting..."
                                      : "Delete"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          {/* =================================================
              EMPTY
          ================================================= */}

          {!isLoading &&
            !error &&
            filteredSchools.length === 0 && (
              <div className="ms-school-admin-state">
                <div className="ms-school-state-icon">
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 10 12 5l9 5-9 5-9-5Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />

                    <path
                      d="M7 14v5h10v-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                  </svg>
                </div>

                <h3>
                  {searchTerm
                    ? "No matching schools"
                    : "No schools found"}
                </h3>

                <p>
                  {searchTerm
                    ? "Try another school name, address or ID."
                    : "No school records are currently available."}
                </p>

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() =>
                      setSearchTerm("")
                    }
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
        </section>
      </div>
    </main>
  );
}